import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Plus, FileSpreadsheet, FileText, ChevronLeft, ChevronRight,
  DollarSign, Trash2, RefreshCw, X, ChevronDown, ChevronUp,
  TrendingUp, TrendingDown, Wallet, Filter, Search, Download
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Modal from '../components/Modal';
import './Accounting.css';

const Accounting = () => {
  const { addNotification } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [movements, setMovements] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [activeListing, setActiveListing] = useState('reducido');
  const [activeMovementType, setActiveMovementType] = useState('todos');

  // Load active staff from localStorage
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const isAdmin = activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o';
  const staffName = activeStaff.full_name || '';
  
  // Filters
  const [fechaDesde, setFechaDesde] = useState(format(new Date().setDate(new Date().getDate() - 30), 'yyyy-MM-dd'));
  const [fechaHasta, setFechaHasta] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedProfessional, setSelectedProfessional] = useState(isAdmin ? 'TODOS' : (activeStaff.full_name || 'TODOS'));
  const [selectedCategory, setSelectedCategory] = useState('TODOS');
  const [searchTerm, setSearchTerm] = useState('');

  const [newMov, setNewMov] = useState({ description: '', amount: '', type: 'ingreso', category: 'Consulta' });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);

  const CAJA_CATEGORIES = ['Consulta', 'Estudio', 'Insumos', 'Servicios', 'Alquiler', 'Otros'];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch professionals for filter
      const { data: profData } = await supabase.from('professionals').select('id, full_name');
      setProfessionals(profData || []);

      // Fetch transactions
      let query = supabase.from('transactions').select('*').order('created_at', { ascending: false });
      
      if (selectedProfessional && selectedProfessional !== 'TODOS') {
        query = query.eq('professional_name', selectedProfessional);
      }
      
      if (activeMovementType === 'ingresos') query = query.eq('type', 'ingreso');
      if (activeMovementType === 'egresos') query = query.eq('type', 'egreso');
      if (selectedCategory !== 'TODOS') query = query.eq('category', selectedCategory);
      if (fechaDesde) query = query.gte('created_at', fechaDesde);
      if (fechaHasta) query = query.lte('created_at', fechaHasta + 'T23:59:59');

      const { data, error } = await query;
      if (error) throw error;
      setMovements(data || []);
    } catch (err) {
      addNotification('Error al cargar contabilidad', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeMovementType, selectedCategory, fechaDesde, fechaHasta, selectedProfessional]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateMovement = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase.from('transactions').insert([{
        description: newMov.description,
        amount: parseFloat(newMov.amount),
        type: newMov.type,
        category: newMov.category,
        created_at: new Date().toISOString(),
        professional_name: staffName
      }]);
      if (error) throw error;
      addNotification('Movimiento registrado correctamente', 'success');
      setShowNewModal(false);
      setNewMov({ description: '', amount: '', type: 'ingreso', category: 'Consulta' });
      fetchData();
    } catch (err) {
      addNotification('Error al registrar', 'error');
    }
  };

  const totals = useMemo(() => {
    const ing = movements.filter(m => m.type === 'ingreso').reduce((s, m) => s + Number(m.amount), 0);
    const egr = movements.filter(m => m.type === 'egreso').reduce((s, m) => s + Number(m.amount), 0);
    return { ing, egr, balance: ing - egr };
  }, [movements]);

  const filteredMovements = movements.filter(m => 
    m.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginated = filteredMovements.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filteredMovements.length / perPage);

  const fmt = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(n);

  return (
    <div className="accounting-page">
      <div className="page-header">
        <div className="title-section">
          <h1>Gestión Contable</h1>
          <p>Auditoría de ingresos y egresos del centro médico.</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => addNotification('Generando reporte...', 'info')}><FileSpreadsheet size={18} /> EXCEL</button>
          <button className="btn-primary" onClick={() => setShowNewModal(true)}><Plus size={18} /> NUEVO MOVIMIENTO</button>
        </div>
      </div>

      <div className="acc-stats-grid">
        <div className="acc-stat-card card glass-panel-premium">
          <div className="stat-icon-circle green"><TrendingUp size={24} /></div>
          <div className="stat-info">
            <span className="label">INGRESOS TOTALES</span>
            <span className="value">{fmt(totals.ing)}</span>
          </div>
        </div>
        <div className="acc-stat-card card glass-panel-premium">
          <div className="stat-icon-circle red"><TrendingDown size={24} /></div>
          <div className="stat-info">
            <span className="label">EGRESOS TOTALES</span>
            <span className="value">{fmt(totals.egr)}</span>
          </div>
        </div>
        <div className="acc-stat-card card glass-panel-premium highlight">
          <div className="stat-icon-circle blue"><Wallet size={24} /></div>
          <div className="stat-info">
            <span className="label">BALANCE NETO</span>
            <span className={`value ${totals.balance >= 0 ? 'text-green' : 'text-red'}`}>
              {fmt(totals.balance)}
            </span>
          </div>
        </div>
      </div>

      <div className="acc-filters-premium card glass-panel">
        <div className="filters-main-row">
          <div className="search-box-premium">
            <Search size={18} />
            <input 
              type="text" 
              placeholder="Buscar por descripción..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="date-filters">
            <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
            <span className="to-arrow">→</span>
            <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
          </div>
          {isAdmin && (
            <select className="acc-select" value={selectedProfessional} onChange={e => setSelectedProfessional(e.target.value)}>
              <option value="TODOS">TODAS LAS ÁREAS</option>
              {professionals.map(p => <option key={p.id} value={p.full_name}>{p.full_name}</option>)}
            </select>
          )}
          <select className="acc-select" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
            <option value="TODOS">TODAS LAS CATEGORÍAS</option>
            {CAJA_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button className="btn-refresh" onClick={fetchData}><RefreshCw size={18} /></button>
        </div>
        
        <div className="filters-tabs-row">
          <div className="tab-group-p">
            {[['todos', 'TODOS'], ['ingresos', 'SOLO INGRESOS'], ['egresos', 'SOLO EGRESOS']].map(([val, lbl]) => (
              <button 
                key={val} 
                className={`tab-btn-p ${activeMovementType === val ? 'active' : ''}`}
                onClick={() => setActiveMovementType(val)}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="acc-table-container card glass-panel-premium">
        <table className="acc-table-v2">
          <thead>
            <tr>
              <th>FECHA</th>
              <th>DESCRIPCIÓN / CONCEPTO</th>
              <th>CATEGORÍA</th>
              <th className="text-right">IMPORTE</th>
              <th className="text-center">ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="text-center-p">Procesando movimientos contables...</td></tr>
            ) : paginated.length === 0 ? (
              <tr><td colSpan="5" className="text-center-p">No se encontraron registros para este período.</td></tr>
            ) : paginated.map(m => (
              <tr key={m.id} className={`acc-row-v2 ${m.type}`}>
                <td className="date-cell">{format(new Date(m.created_at), 'dd/MM/yyyy')}</td>
                <td>
                  <div className="desc-wrap">
                    <span className="desc-main">{m.description}</span>
                    <span className="desc-time">{format(new Date(m.created_at), 'HH:mm')} hs</span>
                  </div>
                </td>
                <td><span className="category-tag">{m.category}</span></td>
                <td className={`text-right amount-cell ${m.type}`}>
                  {m.type === 'ingreso' ? '+' : '-'} {fmt(m.amount)}
                </td>
                <td className="text-center">
                  <button className="btn-delete-acc" onClick={async () => {
                    if (window.confirm('¿Eliminar registro?')) {
                      await supabase.from('transactions').delete().eq('id', m.id);
                      fetchData();
                    }
                  }}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="acc-pagination-footer">
          <div className="pag-info">
            Mostrando {paginated.length} de {filteredMovements.length} movimientos
          </div>
          <div className="pag-controls">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={20} /></button>
            <span className="page-number">Página {page} de {totalPages || 1}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={20} /></button>
          </div>
        </div>
      </div>

      <Modal isOpen={showNewModal} onClose={() => setShowNewModal(false)} title="Nuevo Movimiento Contable">
        <form onSubmit={handleCreateMovement} className="acc-form-premium">
          <div className="form-group-p">
            <label>Descripción del Movimiento</label>
            <input 
              type="text" 
              required 
              placeholder="Ej: Cobro particulares Dr. Rossi"
              value={newMov.description}
              onChange={e => setNewMov({...newMov, description: e.target.value})}
            />
          </div>
          <div className="form-grid-p">
            <div className="form-group-p">
              <label>Importe Total</label>
              <div className="input-with-icon">
                <DollarSign size={16} />
                <input 
                  type="number" 
                  step="0.01" 
                  required 
                  placeholder="0.00"
                  value={newMov.amount}
                  onChange={e => setNewMov({...newMov, amount: e.target.value})}
                />
              </div>
            </div>
            <div className="form-group-p">
              <label>Tipo de Operación</label>
              <select value={newMov.type} onChange={e => setNewMov({...newMov, type: e.target.value})}>
                <option value="ingreso">INGRESO (+)</option>
                <option value="egreso">EGRESO (-)</option>
              </select>
            </div>
          </div>
          <div className="form-group-p">
            <label>Categoría / Rubro</label>
            <select value={newMov.category} onChange={e => setNewMov({...newMov, category: e.target.value})}>
              {CAJA_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-footer-p">
            <button type="button" className="btn-cancel-modal" onClick={() => setShowNewModal(false)}>CANCELAR</button>
            <button type="submit" className="btn-save-p">REGISTRAR EN CAJA</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Accounting;
